/**
 * ADR-0210 Slice 2 — Agents view.
 *
 * The agent is the noun. One table of every AI agent seen on the active
 * site in the window (7d / 30d / 90d) with traffic, sessions, actions,
 * approval, revenue and a trend, and a per-agent page (`AgentDetail`).
 * Reads `GET /v1/sites/:site_id/agent-rollups` — DERIVED daily rollups,
 * refreshed every 15 minutes, never signed records. The "View signed
 * activity" CTA deep-links to Mandates by the agent's display name.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Bot, Search } from 'lucide-react';

import { matchAgentVerdict } from '@sill/types';
import type { AgentDemandResponse, AgentInsight, AgentKind, AgentOverviewResponse, AgentProductRollup, AgentRollupRange, AgentRollupSummary } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { verdictRangeFor } from '../../api/trust-verdicts.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import type { SetView } from '../../app/view-router.js';
import { useSites } from '../../app/providers.js';
import { EmptyStateCard } from '../../components/atoms/EmptyStateCard.js';
import { HoverBars } from '../../components/atoms/HoverBars.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { AgentDetail } from './AgentDetail.js';
import { TrustChip } from './trust/TrustChip.js';
import { useCanDisputeTrust } from './trust/useCanDisputeTrust.js';
import { useTrustVerdicts } from './trust/useTrustVerdicts.js';
import { Demand } from './Demand.js';
import { InsightsStrip } from './InsightsStrip.js';
import { Overview, RefusalsByDay } from './Overview.js';
import { ProtocolMixCard } from '../dashboard/Dashboard.js';
import { VisitHeadline } from './VisitHeadline.js';
import { AgentRowLogo } from './AgentRowLogo.js';
import { Tooltip } from '../../components/atoms/Tooltip.js';
import { visitsLinkFor } from '../mandates/visits-link.js';
import {
  CATEGORY_LABEL,
  KIND_WORDS,
  verdictHint,
  VERDICT_LABEL,
  RANGE_LABEL,
  agentDisplayName,
  fmtDay,
  rangePhrase,
  fmtInt,
  fmtMoney,
  fmtPct,
  monoFont,
  sansFont,
  serifFont,
} from './format.js';

const RANGES: readonly AgentRollupRange[] = ['1d', '7d', '30d', '90d'];
export const KINDS: ReadonlyArray<{ key: AgentKind; label: string; hint: string }> = [
  { key: 'ai', label: KIND_WORDS.ai.label, hint: 'Assistants and answer engines: ChatGPT, Claude, Perplexity, Gemini…' },
  { key: 'bots', label: KIND_WORDS.bots.label, hint: 'Search crawlers, SEO tools, link previews, monitors, scripts' },
  { key: 'humans', label: KIND_WORDS.humans.label, hint: 'Plain browsers — people, plus browser-mode agents that do not identify themselves' },
  { key: 'all', label: KIND_WORDS.all.label, hint: 'Everyone' },
];
export const PAGE_SIZE = 25;
type SortKey = 'traffic' | 'sessions' | 'actions' | 'approved' | 'revenue' | 'last_day' | 'name';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; agents: readonly AgentRollupSummary[]; window: { from_day: string; to_day: string } };

function Sparkline({ daily }: { daily: AgentRollupSummary['daily'] }): JSX.Element {
  return (
    <HoverBars
      width={88}
      height={22}
      bars={daily.map((d) => ({
        value: d.page_loads,
        solid: d.actions,
        tooltip: [fmtDay(d.day), `${fmtInt(d.page_loads)} page loads`, ...(d.actions > 0 ? [`${fmtInt(d.actions)} actions`] : [])],
      }))}
    />
  );
}

/** Empty-state hint that says what would have to happen for this filter + range to show something. */
export function emptyHint(kind: AgentKind, range: AgentRollupRange): string {
  const refresh = range === '1d' ? 'Today refreshes every 15 minutes (UTC day).' : 'Rollups refresh every 15 minutes.';
  switch (kind) {
    case 'ai': return `${refresh} AI agents appear once an assistant such as ChatGPT, Claude or Perplexity loads a page or calls a skill. Try Crawlers & tools or All, or widen the range.`;
    case 'bots': return `${refresh} Crawlers and tools appear once a search engine, SEO tool, link preview or script fetches a page. Try All, or widen the range.`;
    case 'humans': return `${refresh} Human visitors are page loads from plain browsers running the Sill script; they are kept from 2026-09-23 forward. Widen the range.`;
    default: return `${refresh} Nothing has loaded a page or called a skill on this site in the window yet. Widen the range or check back after the next refresh.`;
  }
}

function verdictTone(v: string | null): 'lime' | 'amber' | 'coral' | 'neutral' {
  if (v === 'safe') return 'lime';
  if (v === 'monitor') return 'amber';
  if (v === 'block') return 'coral';
  return 'neutral';
}

export function Agents({ setView }: { setView: SetView }): JSX.Element {
  const C = useC();
  const api = useApi();
  const auth = useOptionalAuth();
  const { activeSite } = useSites();
  // Insight dismissal is owner/admin only server-side (ADR audit
  // 2026-10-02). Reviewer/viewer can view the strip but not clear it;
  // the dashboard hides the dismiss affordances. Legacy test scaffolds
  // that mount without `<AuthProvider>` get a null auth and keep the
  // pre-existing behaviour (dismiss visible).
  const role =
    auth !== null && auth.state.kind === 'authenticated'
      ? auth.state.user.role
      : null;
  const canDismissInsights =
    role === null || role === 'owner' || role === 'admin';
  // ADR-0235 — trust disputes: owner/admin/reviewer, never a support session.
  const canDisputeTrust = useCanDisputeTrust();
  const [range, setRange] = useState<AgentRollupRange>('30d');
  // Deep link from the Dashboard's visitor-mix tiles (`?agent_kind=`), else AI agents.
  const [agentKind, setAgentKind] = useState<AgentKind>(() => {
    if (typeof window === 'undefined') return 'ai';
    try {
      const k = new URL(window.location.href).searchParams.get('agent_kind');
      return k === 'all' || k === 'ai' || k === 'bots' || k === 'humans' ? k : 'ai';
    } catch { return 'ai'; }
  });
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'traffic', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKeyState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try { return new URL(window.location.href).searchParams.get('agent'); }
    catch { return null; }
  });
  // Browser back/forward walks agent selection (founder 2026-09-23: back
  // exited the app). The selected key rides in `?agent=` via pushState;
  // popstate restores it.
  const setSelectedKey = useCallback((k: string | null): void => {
    setSelectedKeyState(k);
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get('agent');
      if (k === null) url.searchParams.delete('agent'); else url.searchParams.set('agent', k);
      if (current !== k) window.history.pushState({ agent: k }, '', url.toString());
    } catch { /* history is enhancement only */ }
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = (): void => {
      try {
        setSelectedKeyState(new URL(window.location.href).searchParams.get('agent'));
      } catch { /* ignore */ }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [products, setProducts] = useState<readonly AgentProductRollup[]>([]);
  const [mode, setMode] = useState<'overview' | 'agents' | 'demand'>(() => {
    // Deep link from the Agent Readiness page (`?agents_mode=`), e.g.
    // Understand → demand, Act → mandates (handled by the setView),
    // Read → agents. Founder 2026-10-20: `probes` moved under
    // Security · Attack paths and is now redirected there by
    // `initialView()`; the Agents view no longer renders a Probes tab.
    if (typeof window === 'undefined') return 'overview';
    try {
      const m = new URL(window.location.href).searchParams.get('agents_mode');
      return m === 'agents' || m === 'demand' || m === 'overview' ? m : 'overview';
    } catch { return 'overview'; }
  });
  const [demand, setDemand] = useState<{ kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'loaded'; data: AgentDemandResponse }>({ kind: 'loading' });
  const [overview, setOverview] = useState<{ kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'loaded'; data: AgentOverviewResponse }>({ kind: 'loading' });
  const [insights, setInsights] = useState<readonly AgentInsight[]>([]);
  // ADR-0235 — null unless this site's trust verdicts are live.
  const trust = useTrustVerdicts(activeSite?.site_id ?? null, verdictRangeFor(range));
  const trustWindow = verdictRangeFor(range) === '7d' ? 'last 7 days' : 'last 30 days';

  const load = useCallback(async (): Promise<void> => {
    if (activeSite === null) return;
    setState({ kind: 'loading' });
    try {
      const res = await api.agentRollups.get(activeSite.site_id, range, undefined, agentKind);
      setState({ kind: 'loaded', agents: res.agents, window: res.window });
      setPage(1);
    } catch (err) {
      setState({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not load agent rollups.' });
    }
  }, [api, activeSite, range, agentKind]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (activeSite === null) return;
    let cancelled = false;
    void api.agentRollups.insights(activeSite.site_id, 7).then((res) => {
      if (!cancelled) setInsights(res.insights);
    }).catch(() => { if (!cancelled) setInsights([]); });
    return () => { cancelled = true; };
  }, [api, activeSite]);

  const dismissInsight = useCallback((insight_ids: readonly string[]): void => {
    if (activeSite === null) return;
    const set = new Set(insight_ids);
    setInsights((cur) => cur.filter((i) => !set.has(i.insight_id)));
    for (const id of insight_ids) {
      void api.agentRollups.dismissInsight(activeSite.site_id, id).catch(() => undefined);
    }
  }, [api, activeSite]);

  useEffect(() => {
    if (activeSite === null || mode !== 'demand') return;
    let cancelled = false;
    setDemand({ kind: 'loading' });
    void api.agentRollups.demand(activeSite.site_id, range, agentKind).then((data) => {
      if (!cancelled) setDemand({ kind: 'loaded', data });
    }).catch((err: unknown) => {
      if (!cancelled) setDemand({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not load demand.' });
    });
    return () => { cancelled = true; };
  }, [api, activeSite, range, mode, agentKind]);

  useEffect(() => {
    if (activeSite === null || (mode !== 'overview' && mode !== 'demand')) return;
    let cancelled = false;
    setOverview({ kind: 'loading' });
    void api.agentRollups.overview(activeSite.site_id, range, agentKind).then((data) => {
      if (!cancelled) setOverview({ kind: 'loaded', data });
    }).catch((err: unknown) => {
      if (!cancelled) setOverview({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not load overview.' });
    });
    return () => { cancelled = true; };
  }, [api, activeSite, range, mode, agentKind]);

  useEffect(() => {
    if (activeSite === null || selectedKey === null) { setProducts([]); return; }
    let cancelled = false;
    void api.agentRollups.get(activeSite.site_id, range, selectedKey, agentKind).then((res) => {
      if (!cancelled) setProducts(res.products ?? []);
    }).catch(() => { if (!cancelled) setProducts([]); });
    return () => { cancelled = true; };
  }, [api, activeSite, range, selectedKey, agentKind]);

  const agents = state.kind === 'loaded' ? state.agents : [];
  const totals = useMemo(() => {
    const t = { visits: 0, sessions: 0, actions: 0, approved: 0, rejected: 0, revenue: 0, currency: null as string | null };
    for (const a of agents) {
      t.visits += a.page_loads; t.sessions += a.sessions; t.actions += a.actions;
      t.approved += a.approved; t.rejected += a.rejected; t.revenue += a.revenue_minor;
      if (t.currency === null && a.currency !== null) t.currency = a.currency;
    }
    return t;
  }, [agents]);

  const selected = selectedKey === null ? null : agents.find((a) => a.agent_key === selectedKey || a.agent_keys.includes(selectedKey)) ?? null;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q.length === 0 ? agents : agents.filter((a) =>
      agentDisplayName(a).toLowerCase().includes(q)
      || (a.agent_id ?? '').toLowerCase().includes(q)
      || (CATEGORY_LABEL[a.category ?? ''] ?? '').toLowerCase().includes(q));
    const val = (a: AgentRollupSummary): number | string => {
      switch (sort.key) {
        case 'traffic': return a.page_loads;
        case 'sessions': return a.sessions;
        case 'actions': return a.actions;
        case 'approved': return a.approved + a.rejected === 0 ? -1 : a.approved / (a.approved + a.rejected);
        case 'revenue': return a.revenue_minor;
        case 'last_day': return a.last_day;
        case 'name': return agentDisplayName(a).toLowerCase();
      }
    };
    const sorted = [...filtered].sort((x, y) => {
      const a = val(x); const b = val(y);
      const c = a < b ? -1 : a > b ? 1 : 0;
      return sort.dir === 'asc' ? c : -c;
    });
    return sorted;
  }, [agents, query, sort]);
  const page_count = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const page_safe = Math.min(page, page_count);
  const page_rows = visible.slice((page_safe - 1) * PAGE_SIZE, page_safe * PAGE_SIZE);
  const toggleSort = (key: SortKey): void => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }));
    setPage(1);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span style={{ fontFamily: monoFont, color: C.muted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>
              {KIND_WORDS[agentKind].label} · {state.kind === 'loaded' ? `${fmtInt(agents.length)} seen ${rangePhrase(range)}` : 'loading'}
            </span>
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 'clamp(36px, 6vw, 52px)', lineHeight: 1, color: C.text, letterSpacing: -1.5 }}>
            Who is visiting, <em>and what they do</em>.
          </h1>
          <VisitHeadline
            overview={overview.kind === 'loaded' ? overview.data : null}
            site_id={activeSite?.site_id ?? null}
            range={range}
            agent_kind={agentKind}
          />
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => { setRange(r); }}
              aria-pressed={range === r}
              className="px-3 py-1.5"
              style={{
                fontFamily: monoFont, fontSize: 10, letterSpacing: 1,
                color: range === r ? C.text : C.muted,
                background: range === r ? C.surface2 : 'transparent',
                border: `1px solid ${range === r ? C.border : 'transparent'}`,
                borderRadius: 2, cursor: 'pointer',
              }}
            >
              {RANGE_LABEL[r].toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3" style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-1" role="tablist" aria-label="Agents or demand">
          {(['overview', 'agents', 'demand'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => { setMode(m); setSelectedKey(null); }}
              className="px-3 py-1.5"
              style={{
                fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase',
                color: mode === m ? C.text : C.muted, background: 'transparent', border: 'none',
                borderBottom: `2px solid ${mode === m ? C.text : 'transparent'}`, cursor: 'pointer',
              }}
            >
              {m === 'overview' ? 'Overview' : m === 'agents' ? 'Agents' : 'Demand'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Which callers">
          {KINDS.map((k) => (
            <Tooltip key={k.key} content={k.hint}>
            <button
              type="button"
              onClick={() => { setAgentKind(k.key); setSelectedKey(null); }}
              aria-pressed={agentKind === k.key}
              className="px-2.5 py-1"
              style={{
                fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase',
                color: agentKind === k.key ? C.text : C.muted,
                background: agentKind === k.key ? C.surface2 : 'transparent',
                border: `1px solid ${agentKind === k.key ? C.border : 'transparent'}`,
                borderRadius: 2, cursor: 'pointer',
              }}
            >
              {k.label}
            </button>
            </Tooltip>
          ))}
        </div>
      </div>

      {(mode === 'overview' || mode === 'agents') && selected === null && agentKind !== 'humans' ? (
        <InsightsStrip
          insights={insights}
          onDismiss={dismissInsight}
          canDismiss={canDismissInsights}
          onOpenAgent={(k) => {
            // The insight carries one UA build's key; the table row may be
            // keyed by a sibling build (identity folding) or be outside the
            // current filter. Find it, widen to All if needed, open it.
            const row = agents.find((a) => a.agent_key === k || a.agent_keys.includes(k));
            if (row === undefined && agentKind !== 'all') setAgentKind('all');
            setMode('agents');
            setSelectedKey(row?.agent_key ?? k);
          }}
        />
      ) : null}

      {mode === 'overview' ? (
        overview.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        ) : overview.kind === 'error' ? (
          <EmptyStateCard icon={Bot} label="Could not load overview" hint={overview.message} />
        ) : overview.data.totals.traffic === 0 && overview.data.prior.traffic === 0 ? (
          <EmptyStateCard icon={Bot} label={`No ${KIND_WORDS[agentKind].plural} ${rangePhrase(range)}`} hint={emptyHint(agentKind, range)} />
        ) : (
          <>
            <Overview data={overview.data} kind={agentKind} onOpenAgent={(k) => { setMode('agents'); setSelectedKey(k); }} />
            {/* ADR-0253 §A — "How agents reach you" protocol-mix card, same
                component the main Dashboard renders. The Agents chip range
                is `AgentRollupRange` ('1d' | '7d' | '30d' | '90d'); the
                reporting hook takes `ReportingRange` ('24h' | '7d' | '30d'
                | '90d' | '1y'). `'1d'` maps to `'24h'`; the rest overlap. */}
            <div className="mt-6">
              <ProtocolMixCard
                range={range === '1d' ? '24h' : range}
                goTo={setView}
              />
            </div>
          </>
        )
      ) : mode === 'demand' ? (
        demand.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
        ) : demand.kind === 'error' ? (
          <EmptyStateCard icon={Bot} label="Could not load demand" hint={demand.message} />
        ) : (
          <>
            {overview.kind === 'loaded' ? <RefusalsByDay rows={overview.data.refusals_by_day} /> : null}
            <Demand data={demand.data} kind={agentKind} range={range} />
          </>
        )
      ) : state.kind === 'error' ? (
        <EmptyStateCard icon={Bot} label="Could not load agents" hint={state.message} />
      ) : selected !== null ? (
        <AgentDetail
          agent={selected}
          products={products}
          trust={trust === null || activeSite === null ? null : {
            verdict: matchAgentVerdict(selected, trust.agents),
            site_id: activeSite.site_id,
            canDispute: canDisputeTrust,
            windowPhrase: trustWindow,
            onDisputed: trust.addDispute,
          }}
          onBack={() => setSelectedKey(null)}
          onViewActivity={() => setView('mandates', visitsLinkFor(selected))}
        />
      ) : state.kind === 'loading' ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>LOADING…</div>
      ) : agents.length === 0 ? (
        <EmptyStateCard icon={Bot} label={`No ${KIND_WORDS[agentKind].plural} ${rangePhrase(range)}`} hint={emptyHint(agentKind, range)} />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" style={{ marginBottom: 16 }}>
            {[
              ['Agents', fmtInt(agents.length)],
              ['Traffic', fmtInt(totals.visits)],
              ['Sessions', fmtInt(totals.sessions)],
              ['Actions', fmtInt(totals.actions)],
              ['Approval', fmtPct(totals.approved, totals.approved + totals.rejected)],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '12px 14px', border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
                <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted }}>{label}</div>
                <div style={{ fontFamily: monoFont, fontSize: 20, fontWeight: 600, color: C.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginBottom: 10 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface, padding: '6px 10px', minWidth: 220 }}>
              <Search size={12} color={C.muted} />
              <input
                type="search"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder="Search agents"
                aria-label="Search agents"
                style={{ background: 'transparent', border: 'none', outline: 'none', fontFamily: sansFont, fontSize: 13, color: C.text, width: '100%' }}
              />
            </label>
            <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}>
              {visible.length === agents.length ? `${fmtInt(agents.length)} agents` : `${fmtInt(visible.length)} of ${fmtInt(agents.length)} agents`}
            </div>
          </div>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 13 }} data-testid="agents-table">
              <thead>
                <tr style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}>
                  {([
                    ['name', 'Agent', 'left'], ['traffic', 'Traffic', 'right'], ['sessions', 'Sessions', 'right'], ['actions', 'Actions', 'right'],
                    ['approved', 'Approved', 'right'], ['revenue', 'Revenue', 'right'], [null, 'Trend', 'left'], ['last_day', 'Last seen', 'left'],
                  ] as ReadonlyArray<[SortKey | null, string, 'left' | 'right']>).map(([key, h, align]) => (
                    <th key={h} style={{ textAlign: align, padding: '12px 14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {key === null ? h : (
                        <button
                          type="button"
                          onClick={() => toggleSort(key)}
                          aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: sort.key === key ? C.text : 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          {h}
                          {sort.key === key ? (sort.dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : null}
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page_rows.map((a) => (
                  <tr
                    key={a.agent_key}
                    data-agent-key={a.agent_key}
                    onClick={() => setSelectedKey(a.agent_key)}
                    style={{ borderTop: `1px solid ${C.borderSoft}`, cursor: 'pointer' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') setSelectedKey(a.agent_key); }}
                    tabIndex={0}
                  >
                    <td style={{ padding: '12px 14px' }}>
                      <div className="flex items-center gap-2" style={{ color: C.text, fontWeight: 500 }}>
                        <AgentRowLogo agent={a} name={agentDisplayName(a)} />
                        <span>{agentDisplayName(a)}</span>
                      </div>
                      <div className="flex items-center gap-1.5" style={{ marginTop: 4 }}>
                        {a.category !== null ? <Pill tone="neutral">{CATEGORY_LABEL[a.category] ?? a.category}</Pill> : null}
                        {a.verdict !== null ? <Tooltip content={verdictHint(a)} displayMode="inline-flex"><Pill tone={verdictTone(a.verdict)}>{VERDICT_LABEL[a.verdict] ?? a.verdict}</Pill></Tooltip> : null}
                        {(() => {
                          if (trust === null || activeSite === null) return null;
                          const tv = matchAgentVerdict(a, trust.agents);
                          return tv === null ? null : (
                            <TrustChip verdict={tv} name={agentDisplayName(a)} site_id={activeSite.site_id} canDispute={canDisputeTrust} windowPhrase={trustWindow} onDisputed={trust.addDispute} />
                          );
                        })()}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(a.page_loads)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(a.sessions)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: a.actions > 0 ? C.text : C.muted }}>{fmtInt(a.actions)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtPct(a.approved, a.approved + a.rejected)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: a.revenue_minor > 0 ? C.text : C.muted }}>{fmtMoney(a.revenue_minor, a.currency)}</td>
                    <td style={{ padding: '12px 14px' }}><Sparkline daily={a.daily} /></td>
                    <td style={{ padding: '12px 14px', fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>{fmtDay(a.last_day)}</td>
                  </tr>
                ))}
                {page_rows.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '18px 14px', fontFamily: sansFont, fontSize: 12.5, color: C.dim }}>No {KIND_WORDS[agentKind].plural} match “{query}”.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {page_count > 1 ? (
            <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginTop: 10 }} data-testid="agents-pagination">
              <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}>
                {fmtInt((page_safe - 1) * PAGE_SIZE + 1)}–{fmtInt(Math.min(page_safe * PAGE_SIZE, visible.length))} of {fmtInt(visible.length)}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" disabled={page_safe <= 1} onClick={() => setPage(page_safe - 1)} className="px-3 py-1.5" style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: page_safe <= 1 ? C.dim : C.text, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, cursor: page_safe <= 1 ? 'default' : 'pointer' }}>PREV</button>
                <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted, padding: '0 6px' }}>{page_safe} / {page_count}</span>
                <button type="button" disabled={page_safe >= page_count} onClick={() => setPage(page_safe + 1)} className="px-3 py-1.5" style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: page_safe >= page_count ? C.dim : C.text, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, cursor: page_safe >= page_count ? 'default' : 'pointer' }}>NEXT</button>
              </div>
            </div>
          ) : null}
        </>
      )}
      <div
        style={{
          marginTop: 24,
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 1.5,
          color: C.dim,
          textTransform: 'uppercase',
        }}
      >
        Derived from daily rollups · refreshed every 15 minutes · not signed records
      </div>
    </div>
  );
}
